import { ref } from "vue";

const pendingRoutePath = ref("");

// ✅ alert도 confirm처럼 state 객체로 관리
const alertState = ref({
  open: false,
  message: "",
  okText: "확인",
  resolver: null, // Promise resolve
});

// ✅ confirm 전역 상태
const confirmState = ref({
  open: false,
  message: "",
  okText: "확인",
  cancelText: "취소",
  resolver: null, // Promise resolve
});

// ✅ (선택) confirm 끝난 뒤 alert를 띄우는 큐
const alertQueue = ref([]);

/**
 * ✅ 내부: alert 띄우기 (state 세팅)
 */
function _openAlert(message, options = {}) {
  alertState.value = {
    open: true,
    message: message || "",
    okText: options.okText || "확인",
    resolver: options.resolver || null,
  };
}

export function useAppAlert() {
  /**
   * ✅ alert (즉시)
   * - confirm이 떠있으면 겹치지 않게 "큐로 보냄" (겹침 방지)
   * - 필요하면 이 정책을 바꿀 수 있음
   */
  const showAlert = (msg, options = {}) => {
    pendingRoutePath.value = "";

    // ✅ confirm이 떠있으면 alert를 큐로
    if (confirmState.value.open) {
      alertQueue.value.push({ msg, options });
      return;
    }

    _openAlert(msg ?? "", options);
  };

  /**
   * ✅ alert + 확인 누르면 이동
   */
  const showAlertAndMove = (msg, path, options = {}) => {
    pendingRoutePath.value = path ?? "";

    if (confirmState.value.open) {
      alertQueue.value.push({ msg, options });
      return;
    }

    _openAlert(msg ?? "", options);
  };

  /**
   * ✅ await showAlertAsync('...')
   * - alert 확인 누를 때까지 대기하고 싶을 때
   */
  const showAlertAsync = (msg, options = {}) => {
    return new Promise((resolve) => {
      showAlert(msg, { ...options, resolver: resolve });
    });
  };

  /**
   * ✅ alert 닫기(확인)
   */
  const closeAlert = () => {
    const r = alertState.value.resolver;

    alertState.value.open = false;
    alertState.value.message = "";
    alertState.value.resolver = null;

    // ✅ showAlertAndMove를 위해 routePath는 여기서 비우지 말고,
    //    watch에서 push 후 비우는 방식이 더 안전함 (원하면 여기서도 가능)

    if (r) r(true);

    // ✅ confirm이 닫힌 상태라면, 큐에 쌓인 alert가 있으면 다음 alert 띄움
    if (!confirmState.value.open && alertQueue.value.length > 0) {
      const next = alertQueue.value.shift();
      _openAlert(next.msg ?? "", next.options ?? {});
    }
  };

  /**
   * ✅ confirm (Promise)
   * - confirm이 떠있으면 새 confirm은 무시(또는 큐로 만들 수도 있음)
   */
  const confirm = (message, options = {}) => {
    return new Promise((resolve) => {
      // ✅ 이미 confirm 떠있으면, 중복 호출 방지
      if (confirmState.value.open) {
        // 필요 시: resolve(false) 처리
        return resolve(false);
      }

      // ✅ alert가 떠있으면 먼저 닫고 confirm 띄우기(겹침 방지)
      if (alertState.value.open) {
        // alert를 강제로 닫아버리면 UX가 싫을 수 있음.
        // 여기서는 "큐로 보내고 confirm 먼저" 정책
        alertQueue.value.unshift({
          msg: alertState.value.message,
          options: { okText: alertState.value.okText },
        });
        alertState.value.open = false;
        alertState.value.message = "";
        alertState.value.resolver = null;
      }

      confirmState.value = {
        open: true,
        message: message || "",
        okText: options.okText || "확인",
        cancelText: options.cancelText || "취소",
        resolver: resolve,
      };
    });
  };

  /**
   * ✅ confirm 버튼 처리
   */
  const resolveConfirm = (result) => {
    const r = confirmState.value.resolver;

    confirmState.value.open = false;
    confirmState.value.message = "";
    confirmState.value.resolver = null;

    if (r) r(!!result);

    // ✅ confirm이 닫힌 직후 alert 큐 처리(겹침 방지 핵심)
    if (alertQueue.value.length > 0) {
      const next = alertQueue.value.shift();
      _openAlert(next.msg ?? "", next.options ?? {});
    }
  };

  return {
    // route 이동
    pendingRoutePath,

    // alert
    alertState,
    showAlert,
    showAlertAsync,
    showAlertAndMove,
    closeAlert,

    // confirm
    confirmState,
    confirm,
    resolveConfirm,
  };
}
